import { LitElement, css, html, nothing, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import sheet from './css/bb-widget.css?inline'
import { makeApi, makeBridgeFetch, type BerryBotApi } from './bbcore/api'
import { ConversationController } from './bbcore/conversation'
import {
  type LogEntry,
  type LogEntryInput,
  MAX_LOG_ENTRIES,
} from './bbcore/log'
import type { ParsedAddress } from './bbcore/types'

import './bb-chat'
import './bb-cart'
import './bb-chat-fulfillment'
import './bb-address-form'
import './bb-chat-modifier'
import type { AddressSuggestion } from './bb-chat-fulfillment'
import { findFixture } from './fixtures/index'
import './bb-menu'
import './bb-log'
import './bb-chat-contact'
import './bb-chat-scheduled-time'
import './bb-storefront-info'
import type { OpeningHours } from './bb-storefront-info'
import {
  asContactDetails,
  asDeliveryAddress,
  asFulfillment,
  asModifiers,
  asScheduledTime,
  asSubmit,
  classify,
  type HintKind,
  isBlocking,
} from './bbcore/hints'

// ── `panels` attribute ────────────────────────────────────────────────
// `chat` = the chat panel, an interactive view on the shared conversation.
// Whether bot hints render as inline tap affordances is controlled by the
// `render-hints` attribute (default true) — not by a separate panel.
export type Panel = 'menu' | 'chat' | 'cart' | 'log' | 'storefront-info'

const DEFAULT_PANELS: Panel[] = ['chat']

function parsePanels(raw: string | null | undefined): Panel[] {
  if (!raw) return DEFAULT_PANELS
  const parts = raw
    .split(',')
    .map((p) => p.trim().toLowerCase())
    .filter(
      (p): p is Panel =>
        p === 'menu' ||
        p === 'chat' ||
        p === 'cart' ||
        p === 'log' ||
        p === 'storefront-info',
    )
  return parts.length ? parts : DEFAULT_PANELS
}

@customElement('bb-widget')
export class BbWidget extends LitElement {
  static styles = css`${unsafeCSS(sheet)}`

  // ── Public attributes ───────────────────────────────────────────────

  @property({ attribute: 'api-base' })
  apiBase = '/relay/api/v1'

  @property({ attribute: 'storefront-id' })
  storefrontId = ''

  @property({ attribute: 'v2-token' })
  v2Token = ''

  @property({ attribute: 'v2-account-id' })
  v2AccountId = ''

  // Optional self-auth: when set, the widget fetches its own JWT from
  // this URL on mount (cache: 'no-store') and refreshes on a 401 from
  // the relay. The endpoint must return JSON shaped like
  // `{ v2Token, accountId, storefrontId?, menuId? }` (see api/auth.js).
  // When set, `v2-token`/`v2-account-id` attributes are ignored — the
  // widget owns its token. When empty (default), the legacy host-driven
  // attribute flow applies.
  @property({ attribute: 'auth-url' })
  authUrl = ''

  @property({ attribute: 'panels' })
  panelsAttr = 'chat'

  // Built-in mobile-only Menu/Chat toggle. Below MOBILE_MAX_WIDTH the
  // menu panel is hidden by CSS (no room beside chat); with `panels`
  // including both menu and chat the widget renders its own toggle so
  // the menu stays reachable for hosts that ship no page-level chrome
  // (the bare /widget/ dev page). Hosts with their own toggle (Oishi's
  // header Menu/Both/Chat) opt out with `mobile-toggle="off"`.
  @property({ attribute: 'mobile-toggle' })
  mobileToggle: 'on' | 'off' = 'on'

  // When true (default), the chat panel renders bot hints as inline tap
  // affordances. Set `render-hints="false"` for the typed-only / voice-
  // equivalent path (no affordance UI; the user types every response).
  // Custom converter so an absent attribute keeps the default (true) — Lit's
  // plain Boolean type would coerce absent → false.
  @property({
    attribute: 'render-hints',
    converter: { fromAttribute: (v) => v !== 'false' },
  })
  renderHints = true

  @property({ attribute: 'menu-id' })
  menuId = ''

  // When true, the widget posts raw user-typed PII (phone, email,
  // address) to the conversation log alongside event labels. Off by
  // default — opt in per-embed only while debugging a flow where the
  // value isn't reliably reaching storage (e.g. contact-gate or
  // address-geocoding regressions). With this off, contact-changed
  // events are dropped; affordance events that carry address text fall
  // back to a length-only shape.
  @property({ attribute: 'pii-logging', type: Boolean })
  piiLogging = false

  @property({ attribute: 'cart-collapsible', type: Boolean })
  cartCollapsible = false

  // How to render the `input_needed:fulfillment_type` hint:
  //   - 'inline' (default) — appears below the bot message in the chat
  //     transcript, like every other affordance.
  //   - 'overlay' — appears as a visually-modal card centered over the
  //     widget shell. Same affordance, same events, different mount
  //     point. The chat panel suppresses its inline render in this mode.
  @property({ attribute: 'fulfillment-render' })
  fulfillmentRender: 'inline' | 'overlay' = 'inline'

  // How to render the order-complete confirmation:
  //   - 'panel' (default) — replaces the chat panel slot with an
  //     inline thank-you screen.
  //   - 'modal' — keeps the existing panels visible (frozen) and
  //     pops a centered modal over the widget shell. Closing the
  //     modal restarts the conversation.
  @property({ attribute: 'thankyou-render' })
  thankyouRender: 'panel' | 'modal' = 'panel'

  // Layout for the fulfillment hint when fulfillment-render="overlay".
  // 'inline' (default) — small pill buttons inside the card.
  // 'stacked' — full-width icon-tile rows. Hosts that want the
  // checkout-modal look set this; the widget stays neutral by default.
  @property({ attribute: 'fulfillment-overlay-layout' })
  fulfillmentOverlayLayout: 'inline' | 'stacked' = 'inline'

  // How to render `confirmation_needed:submit_order` — the order-confirm
  // step.
  //   - 'inline' (default) — a "Place my order" button under the bot
  //     bubble. The user confirms in chat.
  //   - 'overlay' — a centered modal card with a line-item recap, the
  //     fulfillment summary, a "Place my order" CTA, and a dismiss
  //     ("Keep shopping"). The chat panel suppresses its inline button.
  //     Pairs naturally with `fulfillment-render="overlay"` as the
  //     follow-up popup in a multi-step checkout flow.
  @property({ attribute: 'submit-render' })
  submitRender: 'inline' | 'overlay' = 'inline'

  // How to render the `input_needed:contact_details` hint (Trello #126).
  // Symmetric to `fulfillment-render`: inline = phone+email form below
  // the bot message; overlay = centered modal card. Pairs with
  // `submit-render="overlay"` for the multi-step checkout flow (pick
  // fulfillment → enter contact → confirm order).
  @property({ attribute: 'contact-render' })
  contactRender: 'inline' | 'overlay' = 'inline'

  // Layout for the contact hint when contact-render="overlay" — mirrors
  // `fulfillment-overlay-layout`.
  @property({ attribute: 'contact-overlay-layout' })
  contactOverlayLayout: 'inline' | 'stacked' = 'inline'

  // How to render the `confirmation_needed:scheduled_time` hint
  // (Trello #131). Same shape as `fulfillment-render` / `contact-render`:
  // inline = picker below the bot message; overlay = centered modal
  // card. Closed-store rescheduling is a blocking precondition like the
  // others; before this the picker was the only one that stayed inline,
  // which was jarring inside a multi-step overlay checkout.
  @property({ attribute: 'scheduling-render' })
  schedulingRender: 'inline' | 'overlay' = 'inline'

  @property({ attribute: 'scheduling-overlay-layout' })
  schedulingOverlayLayout: 'inline' | 'stacked' = 'inline'

  // Optional geocoder for delivery-address autocomplete. When set,
  // typing into the delivery sub-form fires `fetch-address-suggestions`
  // (debounced 300ms in the affordance) and the widget calls this URL.
  // Currently only Nominatim's JSON-v2 shape is supported (`q` query
  // param, returns an array of `{display_name, lat, lon}`). Leave
  // empty (default) to disable autocomplete — typing falls back to
  // free-form entry + the "use a sample address" button.
  @property({ attribute: 'geocoder-url' })
  geocoderUrl = ''

  // When set, the widget runs in *fixture mode*: it pulls a hand-built
  // state (messages/cart/hint/log) from `./fixtures/index.ts` instead of
  // calling the relay, and skips its normal `controller.start()` boot.
  // Lets `fixtures.html` (and the demo fixture pages) iterate on
  // rendering without driving a real conversation.
  @property({ attribute: 'fixture-id' })
  fixtureId = ''

  // ── State ───────────────────────────────────────────────────────────
  // The conversation (id, transcript, cart, fulfillment, hint, in-flight
  // flags) lives in the controller — the single source of truth every panel
  // reads from. The activity log is a widget-level concern (it also records
  // menu picks / order submits, not just the conversation).

  @state() private log: LogEntry[] = []
  private logSeq = 0

  // Widget's rendered width in CSS pixels. Drives the mobile-vs-desktop
  // layout switch — at narrow widths the cart auto-flips into its
  // floating popup mode so chat can use the full row. 0 until the
  // ResizeObserver fires (initial render uses desktop layout, snaps to
  // mobile if applicable on the next frame).
  @state() private _hostWidth = 0
  private _resizeObserver?: ResizeObserver

  // Which panel the mobile Menu/Chat toggle has selected. Defaults to
  // 'chat' so the initial mobile view matches the no-toggle behaviour
  // (chat takes the full row). Ignored above MOBILE_MAX_WIDTH and when
  // `mobile-toggle="off"`. Public + reflected so hosts (e.g. Oishi's
  // "Browse the Menu" entry) can seed it before the first render.
  @property({ attribute: 'mobile-active-panel', reflect: true })
  mobileActivePanel: 'menu' | 'chat' = 'chat'

  // Below this width (CSS px) the widget is treated as mobile: menu/log
  // panels hide via container queries; cart auto-floats. Picked to match
  // the @container (max-width: 640px) breakpoint in bb-widget.css.
  private static readonly MOBILE_MAX_WIDTH = 640

  // Self-auth state. `tokenReady` gates the `api` getter so the controller's
  // hostConnected→start() boot path no-ops until we have a token. Defaults
  // to true (the common path: host stamps the token attribute or no auth at
  // all). connectedCallback flips it to false BEFORE super runs only when
  // we're about to self-auth and don't yet have a token — otherwise super's
  // hostConnected→start() would fire ahead of our bootstrap. An in-flight
  // refresh promise is reused so concurrent 401s don't fan out into N
  // /api/auth calls.
  @state() private tokenReady = true
  private refreshInflight: Promise<string> | null = null

  // Storefront info (name + operational status + opening hours), surfaced
  // by the <bb-storefront-info> strip above the cart. Carried through the
  // /api/auth response (which proxies core-v2's /storefronts under the
  // hood) — the widget never asks berrybot for any of this.
  @state() private storefrontName = ''
  @state() private operationalStatus: 'open' | 'closed' | 'inactive' | null = null
  @state() private openingHours: OpeningHours | null = null
  @state() private storefrontTimeZone = ''

  private controller = new ConversationController(this, () => this.api, {
    getStorefrontId: () => this.storefrontId || undefined,
    onStart: (id, response) => {
      this.appendLog({ kind: 'note', subject: 'conversation.started', text: id })
      // Berrybot uses stable hint ids ("submit_order", "delivery_address",
      // "fulfillment_type"), so a dismissal flag set in the previous
      // conversation would suppress every future overlay in this widget
      // instance. Reset on every conversation start. (The fulfillment flag
      // has a narrower re-attempt-submit reset at submitOrder(); this
      // covers the new-conversation path uniformly.)
      this._submitOverlayDismissedFor = null
      this._addressOverlayDismissedFor = null
      this._fulfillmentOverlayDismissedFor = null
      // Latched once per conversation. Both events need a conversation
      // id (the relay POST is conv-scoped), so we wait until start()
      // succeeds — auth success is implied by reaching this callback.
      this.postEvent('widget.mounted', {
        panels: this.panelsAttr,
        renderHints: this.renderHints,
        fulfillmentRender: this.fulfillmentRender,
        submitRender: this.submitRender,
        hasAuthUrl: Boolean(this.authUrl),
        piiLogging: this.piiLogging,
        hostname:
          typeof location !== 'undefined' ? location.hostname : null,
      })
      this.postEvent('auth.bootstrap', { ok: true })
      this.dispatchEvent(
        new CustomEvent('conversation-started', {
          detail: { conversationId: id, response },
          bubbles: true,
          composed: true,
        }),
      )
    },
    onResponse: (response) => {
      if (response.hint) this.appendLog({ kind: 'hint', hint: response.hint })
      const kind = classify(response.hint)
      const hintId = response.hint?.id ?? null
      this.postEvent('hint.classified', {
        type: response.hint?.type ?? null,
        subject: response.hint?.subject ?? null,
        id: hintId,
        kind,
        willRenderAs: this.predictRenderAs(kind, hintId),
      })
      this.dispatchEvent(
        new CustomEvent('state-change', {
          detail: { response },
          bubbles: true,
          composed: true,
        }),
      )
    },
  })

  // ── Imperative API for the host ─────────────────────────────────────

  send(text: string): Promise<void> {
    return this.controller.send(text)
  }

  restart(): Promise<void> {
    return this.controller.start()
  }

  // Cart mutation surface, exposed publicly so an externally-mounted
  // <bb-cart widget="…"> (or any host script) can drive the same flows the
  // widget's internal cart panel does. All four delegate to the controller —
  // the controller is still the single source of truth for cart state.
  cartAdd(itemName: string, quantity = 1, opts: { price?: string } = {}): Promise<void> {
    if (!itemName) return Promise.resolve()
    return this.controller.addToCart(
      { item_name: itemName, quantity },
      opts.price ? { price: opts.price } : {},
    )
  }

  cartRemove(itemName: string, quantity?: number): Promise<void> {
    if (!itemName) return Promise.resolve()
    return this.controller.removeFromCart(
      quantity ? { item_name: itemName, quantity } : { item_name: itemName },
    )
  }

  clearCart(): Promise<void> {
    return this.controller.clearCart()
  }

  // Public submit: always route through the chat agent so the backend
  // walks the precondition chain (cart → fulfillment → contact → submit)
  // and emits whichever input_needed/confirmation_needed hint is next.
  // The old shortcut that bypassed the agent when fulfillment was already
  // set is gone — it also bypassed the contact gate, which is how Trello
  // #126's "one order submits without contact, another asks for it"
  // divergence happened. The submit_order overlay (renderSubmitOverlay)
  // remains the single final-confirm UI: it renders when the backend
  // returns the submit_order hint, which now requires both fulfillment
  // AND contact to be set.
  submitOrder(): Promise<void> {
    this.appendLog({
      kind: 'note',
      subject: 'order.submit-clicked',
      text: this.controller.conversationId ?? '',
    })
    // Re-attempting submit is an explicit request to walk the chain
    // again, so drop any prior overlay dismissals. Hint ids are stable
    // across turns (e.g. "fulfillment_type", "contact_details") so
    // without this reset a once-cancelled overlay would stay hidden
    // for the rest of the conversation.
    this._fulfillmentOverlayDismissedFor = null
    this._contactOverlayDismissedFor = null
    this._submitOverlayDismissedFor = null
    // When the backend has already walked the precondition chain and is
    // emitting confirmation_needed:submit_order, the click IS the final
    // confirmation — call controller.submitOrder() directly (same wire
    // path as renderSubmitOverlay's onSubmitOverlayConfirm). Routing this
    // through controller.send() re-triggers review_order, which re-emits
    // the same submit_order hint, and the affordance loops forever
    // (Adri prod + test screenshots, 2026-07). Otherwise (no submit_order
    // hint yet — cart-side click before fulfillment / contact are set),
    // keep sending a checkout utterance so the agent walks the chain and
    // emits the next input_needed hint. Preserves d676d35 (Submit live
    // before fulfillment) and #126 (contact gate honoured server-side).
    if (asSubmit(this.controller.hint)) {
      return this.controller.submitOrder()
    }
    return this.controller.send("I'd like to check out.")
  }

  // Snapshot of the controller-owned state that any cart-like panel needs.
  // Re-built each emit; consumers compare by value via Lit's @property
  // setters, so identical snapshots are no-ops downstream.
  get state() {
    const c = this.controller
    return {
      conversationId: c.conversationId,
      cart: c.cart,
      fulfillmentType: c.fulfillmentType,
      hint: c.hint,
      busy: c.busy,
      submitting: c.submitting,
      conversationState: c.conversationState,
      complete: c.conversationState === 'complete',
    }
  }

  // Fire after every Lit render — the controller calls requestUpdate()
  // after every mutation (optimistic cart patch, hint flip, submit, …),
  // so this catches optimistic + relay-driven changes alike. The older
  // `state-change` event (fires only on relay reply, dispatches `{response}`)
  // is left untouched for backwards compatibility.
  // Last-observed controller state used by `updated()` to detect
  // *transitions* that warrant pulling focus to chat on mobile — a new
  // error after no error, or a fresh blocking hint id we haven't seen.
  // Tracking ids (not equality) keeps stable re-renders from re-firing.
  private _prevErrorMsg: string | null = null
  private _prevBlockingHintId: string | null = null
  // Trello #111: chat panel is force-included while a modifier-picker
  // hint is active and the host's `panels` doesn't list `chat` (so the
  // picker has nowhere to render otherwise). See
  // `_maybeFocusChatOnAttention` / `_clearModifierChatOverrideIfDone`.
  @state() private _chatForcedByModifierHint = false

  updated(changed: Map<string, unknown>) {
    super.updated(changed)
    // Safety net: when the widget mounts inside display:none chrome
    // (Oishi orderPage starts hidden), ResizeObserver may not fire
    // before our first render set _hostWidth=0 — leaving _isMobile
    // false and the wrong cart variant rendered until something else
    // triggers an update. Self-measure on any reactive tick once
    // we're visible, so we don't depend on RO catching up.
    if (this._hostWidth === 0 && this.offsetParent !== null) {
      const w = this.getBoundingClientRect().width
      if (w > 0) this._hostWidth = w
    }
    this._maybeFocusChatOnAttention()
    this._clearModifierChatOverrideIfDone()
    this._maybeHoldOverlayForNewBotTurn()
    const lastMsg =
      this.controller.messages[this.controller.messages.length - 1]
    this.toggleAttribute(
      'data-modifier-active',
      !this.controller.busy &&
        lastMsg?.who === 'bot' &&
        !!asModifiers(lastMsg.hint),
    )
    this.dispatchEvent(
      new CustomEvent('bb-state-change', {
        detail: this.state,
        bubbles: true,
        composed: true,
      }),
    )
  }

  // On mobile, when the backend surfaces something the user must read or
  // act on (cart/add error, blocking hint), flip the Menu/Chat toggle to
  // Chat so the message isn't hidden behind the menu tab. No-op on
  // desktop and when the toggle is opted out.
  //
  // ALSO (Trello #111): the modifier picker only renders inside `bb-chat`.
  // When the host's `panels` strips chat (e.g. Oishi's "Menu only" header
  // toggle), clicking a customizable item silently sets a modifier hint
  // with nowhere to render. Force chat into the effective panel set —
  // alongside menu on wide, taking over on narrow — for the lifetime of
  // the picker. Cleared by `_clearModifierChatOverrideIfDone` once the
  // hint dismisses; the host's original `panels` resumes ownership.
  private _maybeFocusChatOnAttention() {
    const c = this.controller
    const err = c.error ? String(c.error) : null
    const hint = c.hint
    const blockingId = hint && isBlocking(hint) ? String(hint.id ?? hint.subject ?? '') : null

    const newError = !!err && err !== this._prevErrorMsg
    const newBlocking = !!blockingId && blockingId !== this._prevBlockingHintId

    this._prevErrorMsg = err
    this._prevBlockingHintId = blockingId

    if (!newError && !newBlocking) return

    const newModifier = !!asModifiers(hint)
    const chatMissing = !this.panels.includes('chat')
    if (newModifier && chatMissing) {
      this._chatForcedByModifierHint = true
      if (this.mobileToggle !== 'off') this.mobileActivePanel = 'chat'
      return
    }

    if (this.mobileToggle === 'off') return
    if (!this._isMobile) return
    if (!this.panels.includes('chat') || !this.panels.includes('menu')) return
    if (this.mobileActivePanel === 'chat') return
    this.mobileActivePanel = 'chat'
  }

  // Mirror of the above: drop the chat override as soon as the modifier
  // hint clears (picker confirmed → backend processes the utterance →
  // hint disappears; or the customer dismissed it). Runs on every update,
  // so cancel/confirm both deactivate the override on the next tick.
  private _clearModifierChatOverrideIfDone() {
    if (!this._chatForcedByModifierHint) return
    if (asModifiers(this.controller.hint)) return
    this._chatForcedByModifierHint = false
  }

  // ── Leave-guard ────────────────────────────────────────────────────
  // Block a tab close / refresh when the user has something to lose:
  // a non-empty cart that hasn't been submitted, OR a submit currently
  // in flight. Browsers ignore the message body (Chrome shows a generic
  // "Leave site?" dialog regardless), but the cancellation is what
  // actually prompts the dialog — that's the whole point.
  private onBeforeUnload = (e: BeforeUnloadEvent) => {
    // Fire-and-forget telemetry. Fetch with keepalive is the standard
    // pattern for unload-time POSTs (regular fetch is cancelled when the
    // page unloads). postClientEvent doesn't request keepalive today,
    // but the event still has best-effort odds of landing on slow
    // unloads — and on the common case (user closes a tab with no
    // guarded state), the rest of the unload pause lets it complete.
    this.postEvent('page.beforeunload', {
      guarded: this.shouldGuardLeave(),
    })
    if (!this.shouldGuardLeave()) return
    e.preventDefault()
    e.returnValue = ''
  }

  private shouldGuardLeave(): boolean {
    // Disabled: the native "Leave site?" dialog is more friction than
    // protection (it blocks every reload during dev/testing too), and
    // an abandoned-cart recovery is better handled out-of-band rather
    // than via a browser-stock modal the customer can't act on. The
    // `page.beforeunload` telemetry above still fires unconditionally
    // so we keep visibility into unload events.
    return false
  }

  connectedCallback() {
    // Gate BEFORE super: the controller's hostConnected → start() fires
    // inside super.connectedCallback(). If we're going to self-auth and
    // have no token yet, hold start now and let bootstrapAuth kick it.
    if (!this.fixtureId && this.authUrl && !this.v2Token) {
      this.tokenReady = false
    }
    super.connectedCallback()
    window.addEventListener('beforeunload', this.onBeforeUnload)
    const vv = (window as { visualViewport?: VisualViewport }).visualViewport
    if (vv) {
      vv.addEventListener('resize', this.onVisualViewportResize)
      vv.addEventListener('scroll', this.onVisualViewportResize)
    }
    this._resizeObserver = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width
      if (typeof w === 'number') this._hostWidth = w
    })
    this._resizeObserver.observe(this)
    // set-contact bubbles from <bb-chat-contact> (composed) when the
    // user submits the in-widget contact form. Trello #126: contact is
    // a first-class precondition driven by `input_needed:contact_details`
    // — the old slot-based `contact-ready` / `contact-submit` /
    // `contact-changed` host listeners are gone.
    this.addEventListener('set-contact', this.onSetContact)
    // schedule-order / schedule-decline bubble from
    // <bb-chat-scheduled-time> (Trello #131). Same text-first contract
    // the old inline renderer had — the widget sends a plain utterance
    // and the agent's schedule_order tool validates.
    this.addEventListener('schedule-order', this.onScheduleOrder)
    this.addEventListener('schedule-decline', this.onScheduleDecline)
    this.addEventListener('widget-affordance', this.onAffordance)
    window.addEventListener('keydown', this.onWindowKeyDown)
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange)
    }
    if (this.fixtureId) this.seedFromFixture(this.fixtureId)
    // Self-auth mount path: only do the bootstrap fetch when the host
    // hasn't already stamped a token on us. Hosts that prefetch /api/auth
    // for other page consumers (e.g. window.__bb) can pass both the
    // token attr AND auth-url — we'll skip the duplicate fetch and only
    // use the refresh hook for 401 recovery.
    else if (this.authUrl && !this.v2Token) void this.bootstrapAuth()
  }

  // First-time token load. The controller is already registered, so its
  // hostConnected has already called start() — but the api getter returned
  // null (tokenReady=false), so start() bailed. Once we have a token, flip
  // the flag and call start() ourselves.
  private async bootstrapAuth(): Promise<void> {
    try {
      await this.fetchAuth()
      this.tokenReady = true
      void this.controller.start()
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      this.appendLog({
        kind: 'note',
        subject: 'auth.failed',
        text: msg,
      })
      // No conversation id yet → can't use /client_events. Fall back to
      // the relay's anonymous _log endpoint, keyed off hostname so we
      // can grep `pm2 logs ai-ordering-relay-api-test` by site when the
      // gate blocks every load on a given embed.
      void this.postAnonymousLog('auth.failed', { error: msg })
    }
  }

  private async postAnonymousLog(
    event: string,
    data: Record<string, unknown>,
  ): Promise<void> {
    try {
      await fetch(`${this.apiBase}/_log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event,
          ts_client: new Date().toISOString(),
          page: typeof location !== 'undefined' ? location.hostname : '',
          data,
        }),
      })
    } catch {
      // Telemetry must not throw.
    }
  }

  // Single-flight refresh used by both bootstrap and the 401 backstop.
  // Returns the new token.
  private fetchAuth(): Promise<string> {
    if (this.refreshInflight) return this.refreshInflight
    this.refreshInflight = (async () => {
      const r = await fetch(this.authUrl, { cache: 'no-store' })
      if (!r.ok) throw new Error(`auth ${r.status}`)
      const c = (await r.json()) as {
        v2Token?: string
        accountId?: string
        storefrontId?: string
        menuId?: string
        storefrontName?: string | null
        operationalStatus?: 'open' | 'closed' | 'inactive' | null
        openingHours?: OpeningHours | null
        timeZone?: string | null
      }
      if (!c.v2Token) throw new Error('auth response missing v2Token')
      this.v2Token = c.v2Token
      if (c.accountId) this.v2AccountId = c.accountId
      // Only adopt storefront/menu if the host didn't pin them as attrs.
      if (c.storefrontId && !this.storefrontId) this.storefrontId = c.storefrontId
      if (c.menuId && !this.menuId) this.menuId = c.menuId
      this.adoptStorefrontInfo(c)
      return c.v2Token
    })()
    this.refreshInflight.finally(() => {
      this.refreshInflight = null
    })
    return this.refreshInflight
  }

  private adoptStorefrontInfo(c: {
    storefrontName?: string | null
    operationalStatus?: 'open' | 'closed' | 'inactive' | null
    openingHours?: OpeningHours | null
    timeZone?: string | null
  }) {
    if (typeof c.storefrontName === 'string') this.storefrontName = c.storefrontName
    if (c.operationalStatus === 'open' || c.operationalStatus === 'closed' || c.operationalStatus === 'inactive') {
      this.operationalStatus = c.operationalStatus
    }
    if (c.openingHours !== undefined) this.openingHours = c.openingHours
    if (typeof c.timeZone === 'string') this.storefrontTimeZone = c.timeZone
  }

  // When the soft keyboard opens on iOS, layout viewport stays put but
  // visualViewport.height shrinks. A modal centered with
  // `place-items: center` in the widget's absolute box can end up half
  // behind the keyboard. Push a CSS var with the keyboard overlap so
  // `.bb-modal-card` can translate up; default 0 (no offset). Hosts
  // that need precision can read `--bb-keyboard-inset` directly.
  private onVisualViewportResize = () => {
    const vv = (window as { visualViewport?: VisualViewport }).visualViewport
    if (!vv) return
    const overlap = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
    this.style.setProperty('--bb-keyboard-inset', `${overlap}px`)
  }

  // Statically imported (rather than dynamic) so every demo server that
  // routes /widget.js → bb-widget.js works without also having to serve
  // a separate chunk file. Fixtures are tiny; ~1-2KB in the main bundle
  // beats the per-demo plumbing cost of code-splitting.
  private seedFromFixture(id: string) {
    const fixture = findFixture(id)
    if (!fixture) {
      console.warn(`[bb-widget] no fixture with id="${id}"`)
      return
    }
    const s = fixture.state
    if (s.messages !== undefined) this.controller.messages = s.messages
    if (s.cart !== undefined) this.controller.cart = s.cart
    if (s.hint !== undefined) this.controller.hint = s.hint
    if (s.fulfillmentType !== undefined)
      this.controller.fulfillmentType = s.fulfillmentType
    if (s.conversationState !== undefined)
      this.controller.conversationState = s.conversationState
    if (s.orderId !== undefined) this.controller.orderId = s.orderId
    if (s.submitting !== undefined) this.controller.submitting = s.submitting
    if (s.logEntries !== undefined) this.log = s.logEntries
    if (fixture.panels) this.panelsAttr = fixture.panels
    if (fixture.fulfillmentRender)
      this.fulfillmentRender = fixture.fulfillmentRender
    if (fixture.thankyouRender) this.thankyouRender = fixture.thankyouRender
    if (fixture.submitRender) this.submitRender = fixture.submitRender
    this.requestUpdate()
  }

  disconnectedCallback() {
    window.removeEventListener('beforeunload', this.onBeforeUnload)
    window.removeEventListener('keydown', this.onWindowKeyDown)
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange)
    }
    const vv = (window as { visualViewport?: VisualViewport }).visualViewport
    if (vv) {
      vv.removeEventListener('resize', this.onVisualViewportResize)
      vv.removeEventListener('scroll', this.onVisualViewportResize)
    }
    this._resizeObserver?.disconnect()
    this._resizeObserver = undefined
    super.disconnectedCallback()
  }

  // True once the widget has measured itself AND its width is below the
  // mobile breakpoint. Guarded on _hostWidth > 0 so the first render
  // (before ResizeObserver fires) uses the desktop layout rather than
  // briefly flashing mobile.
  private get _isMobile(): boolean {
    return this._hostWidth > 0 && this._hostWidth < BbWidget.MOBILE_MAX_WIDTH
  }

  // ── Internals ───────────────────────────────────────────────────────

  private get panels(): Panel[] {
    return parsePanels(this.panelsAttr)
  }

  private appendLog(input: LogEntryInput) {
    const entry = { ...input, id: ++this.logSeq, at: Date.now() } as LogEntry
    const next = [...this.log, entry]
    this.log =
      next.length > MAX_LOG_ENTRIES ? next.slice(-MAX_LOG_ENTRIES) : next
  }

  private get api(): BerryBotApi | null {
    // Fixture mode: no API at all. Controller's `getApi() → null` short-
    // circuits start() / send() / cart mutations so a hand-built state
    // survives untouched and no spurious /conversations call sneaks out
    // via the controller's hostConnected → start() boot path.
    if (this.fixtureId) return null
    // Self-auth in flight: hold the controller's start() boot path until
    // we have a token. Without this gate, hostConnected would call
    // /conversations with an empty X-V2-Token and 401.
    if (this.authUrl && !this.tokenReady) return null
    const fetcher = makeBridgeFetch({
      apiBase: this.apiBase,
      storefrontId: this.storefrontId,
      getToken: () => this.v2Token,
      getAccountId: () => this.v2AccountId || null,
      // 401 backstop. Only wired when the widget owns its token —
      // hosts that stamp the token attribute themselves are
      // responsible for their own refresh strategy.
      refreshToken: this.authUrl ? () => this.fetchAuth() : undefined,
      onCall: (info) => this.appendLog({ kind: 'http', ...info }),
    })
    return makeApi(fetcher)
  }

  private onSendUtterance = (e: Event) => {
    const text = (e as CustomEvent).detail?.text as string | undefined
    if (text) void this.controller.send(text)
  }

  // Modifier picker confirmed: bypass the LLM and POST directly to
  // /cart/add (Trello #124). The direct endpoint doesn't trigger the
  // backend's extras-upsell heuristic, so optional groups the customer
  // skipped in the picker won't be re-prompted by the bot. The
  // SystemMessage the backend writes keeps the LLM aware on next turns.
  private onModifierAdd = (e: Event) => {
    const d = (e as CustomEvent).detail as
      | {
          itemName: string
          quantity: number
          price?: string
          options: string[]
          extras: string[]
          removals: string[]
        }
      | undefined
    if (!d?.itemName) return
    void this.controller.addToCart(
      {
        item_name: d.itemName,
        quantity: d.quantity,
        ...(d.options.length ? { options: d.options } : {}),
        ...(d.extras.length ? { extras: d.extras } : {}),
        ...(d.removals.length ? { removals: d.removals } : {}),
      },
      d.price ? { price: d.price } : {},
    )
  }

  private onSetFulfillment = (e: Event) => {
    const detail = (e as CustomEvent).detail as
      | {
          fulfillmentType?: 'pick_up' | 'delivery'
          address?: string
          parsedAddress?: Record<string, unknown>
        }
      | undefined
    const ft = detail?.fulfillmentType
    if (ft !== 'pick_up' && ft !== 'delivery') return
    void this.controller.setFulfillment(
      ft === 'delivery' && detail?.address
        ? {
            fulfillment_type: 'delivery',
            address: detail.address,
            ...(detail.parsedAddress ? { parsed_address: detail.parsedAddress } : {}),
          }
        : { fulfillment_type: ft },
    )
  }

  private onFetchSampleAddress = async (e: Event) => {
    const fill = (e as CustomEvent).detail?.fill as
      | ((address: string, parsedAddress?: Record<string, unknown>) => void)
      | undefined
    if (typeof fill !== 'function') return
    const sample = await this.controller.requestSampleAddress()
    if (sample) fill(sample.address, sample.parsedAddress)
  }

  // Delivery-address autocomplete. The affordance debounces and asks
  // for suggestions via this event. Two backends, tried in order:
  //   1. Google Places via the relay (`controller.addressAutocomplete`).
  //      Storefront-biased server-side; API key stays off the client.
  //      Returns place_id; coords are fetched lazily on pick via
  //      `fetch-place-details` (one Details call per pick).
  //   2. Host-configured `geocoder-url` (Nominatim JSON-v2). Inline
  //      coords on every suggestion, no separate details call.
  // No-op only when both are unavailable.
  private onFetchAddressSuggestions = async (e: Event) => {
    const detail = (e as CustomEvent).detail as
      | { query?: string; fill?: (s: AddressSuggestion[]) => void }
      | undefined
    const fill = detail?.fill
    const query = detail?.query?.trim()
    if (typeof fill !== 'function' || !query) return
    // 1. Places via the relay. addressAutocomplete returns [] on any
    // failure (key missing, network blip, etc.) — fall through to
    // Nominatim instead of leaving the dropdown empty.
    const places = await this.controller.addressAutocomplete(query)
    if (places.length) {
      fill(
        places.map((p) => ({
          label: p.description || p.main_text || '',
          address: p.description || p.main_text || '',
          placeId: p.place_id,
        })),
      )
      return
    }
    if (!this.geocoderUrl) {
      fill([])
      return
    }
    try {
      const url = new URL(this.geocoderUrl, location.href)
      url.searchParams.set('q', query)
      url.searchParams.set('format', 'jsonv2')
      url.searchParams.set('limit', '5')
      url.searchParams.set('addressdetails', '1')
      const r = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
      })
      if (!r.ok) {
        fill([])
        return
      }
      const raw = (await r.json()) as Array<{
        display_name?: string
        lat?: string
        lon?: string
      }>
      fill(
        raw
          .filter((x) => x.display_name && x.lat && x.lon)
          .map((x) => ({
            label: x.display_name!,
            address: x.display_name!,
            parsedAddress: {
              formatted_address: x.display_name!,
              coordinates: {
                latitude: Number(x.lat),
                longitude: Number(x.lon),
              },
            },
          })),
      )
    } catch {
      fill([])
    }
  }

  // Places-pick → fetch coords + address_components via Place Details.
  // Fired by bb-chat-fulfillment only for suggestions that carry a
  // place_id (Nominatim path includes coords inline and doesn't fire
  // this). Lazy by design: one Details call per pick, never per
  // keystroke. address_components drives the field-level form so the
  // customer adds any missing piece (typically the house number on a
  // `route` pick) before submit.
  private onFetchPlaceDetails = async (e: Event) => {
    const detail = (e as CustomEvent).detail as
      | {
          placeId?: string
          fill?: (
            parsed?: ParsedAddress,
            components?: Array<{
              types?: string[]
              long_name?: string
              short_name?: string
            }>,
          ) => void
        }
      | undefined
    const fill = detail?.fill
    const placeId = detail?.placeId
    if (typeof fill !== 'function' || !placeId) return
    const d = await this.controller.addressDetails(placeId)
    if (!d || !d.coordinates) {
      fill(undefined, d?.address_components)
      return
    }
    fill(
      {
        formatted_address: d.formatted_address ?? undefined,
        coordinates: {
          latitude: d.coordinates.latitude,
          longitude: d.coordinates.longitude,
        },
      },
      d.address_components,
    )
  }

  private onCartAdd = (e: Event) => {
    const detail = (e as CustomEvent).detail as
      | { itemName?: string; quantity?: number }
      | undefined
    if (!detail?.itemName) return
    void this.cartAdd(detail.itemName, detail.quantity ?? 1)
  }

  private onCartRemove = (e: Event) => {
    const detail = (e as CustomEvent).detail as
      | { itemName?: string; quantity?: number }
      | undefined
    if (!detail?.itemName) return
    void this.cartRemove(detail.itemName, detail.quantity)
  }

  private onCartClear = () => {
    void this.clearCart()
  }

  private onMenuItemSelected = (e: Event) => {
    const detail = (e as CustomEvent).detail as
      | {
          item?: {
            id?: string
            name?: string
            price?: string
            modifierGroups?: Array<{
              name: string
              groupType?: 'choices' | 'adjustments'
              minSelections: number
              maxSelections: number
              options: Array<{ name: string; modifierType?: string }>
            }>
          }
        }
      | undefined
    const item = detail?.item
    if (!item?.name) return
    this.appendLog({ kind: 'note', subject: 'menu.item-selected', text: item.name })
    const groups = item.modifierGroups ?? []
    if (groups.length > 0) {
      // Configurable item — open the in-chat picker instantly with a
      // synthetic `choice_needed:modifiers` hint instead of round-
      // tripping `/cart/add`. The picker confirm wires through the
      // existing `modifier-confirmed` path (agent processes the
      // utterance and adds via its own add_to_cart tool).
      this.controller.openModifierPicker({
        itemId: item.id ?? '',
        itemName: item.name,
        ...(item.price ? { price: item.price } : {}),
        groups: groups.map((g) => ({
          name: g.name,
          // 'adjustments' is the menu's removal marker → 'removal' in the
          // hint so the picker phrases it "without …" (Trello #112).
          groupType: g.groupType === 'adjustments' ? 'removal' : 'choice',
          minSelections: g.minSelections,
          maxSelections: g.maxSelections,
          options: g.options,
        })),
      })
      return
    }
    // Simple item — direct add. The `price` hint lets the optimistic
    // cart line show its unit price immediately before the agent's next
    // turn returns the canonical priced cart.
    void this.controller.addToCart(
      { item_name: item.name, quantity: 1 },
      item.price ? { price: item.price } : {},
    )
  }

  private onSubmitOrder = () => {
    void this.submitOrder()
  }

  private onLogClear = () => {
    this.log = []
  }

  // The chat panel bound to the shared conversation. The `render-hints`
  // attribute controls whether bot hints render as inline tap affordances or
  // the user types every response (the typed-only / voice-equivalent path).
  private renderChat() {
    const c = this.controller
    return html`
      <bb-chat
        exportparts="chat-title"
        .messages=${c.messages}
        .error=${c.error}
        .busy=${c.busy}
        .renderHints=${this.renderHints}
        .suppressFulfillment=${this.fulfillmentRender === 'overlay'
          && !this.isFulfillmentOverlayDismissed()
          && !this.isOverlayHeldForCurrentTurn()}
        .suppressContact=${this.contactRender === 'overlay'
          && !this.isContactOverlayDismissed()
          && !this.isOverlayHeldForCurrentTurn()}
        .suppressScheduling=${this.schedulingRender === 'overlay'
          && !this.isSchedulingOverlayDismissed()
          && !this.isOverlayHeldForCurrentTurn()}
        .suppressSubmit=${this.submitRender === 'overlay'
          && !this.isSubmitOverlayDismissed()
          && !this.isOverlayHeldForCurrentTurn()}
        @send-utterance=${this.onSendUtterance}
        @modifier-add=${this.onModifierAdd}
        @set-fulfillment=${this.onSetFulfillment}
        @submit-order=${this.onSubmitOrder}
        @fetch-sample-address=${this.onFetchSampleAddress}
        @fetch-address-suggestions=${this.onFetchAddressSuggestions}
        @fetch-place-details=${this.onFetchPlaceDetails}
      ></bb-chat>
    `
  }

  // Human label for the scheduled-for ISO. Keeps the parse forgiving:
  // accepts both tz-aware ("…+02:00") and naive store-local ISOs (the
  // picker emits the latter). Returns null when unparseable so callers
  // can fall back to the ASAP copy rather than render "Invalid Date".
  private formatScheduledFor(iso: string): string | null {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return null
    return d.toLocaleString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  }

  // Replaces the chat panel slot with a thank-you screen once the order
  // is placed. One source of truth for "we're done" — see option (a) in
  // the order-lifecycle UX discussion. Copy varies on scheduled vs ASAP
  // so a future-scheduled order doesn't falsely claim the kitchen has
  // already started.
  private renderThankYou() {
    const orderId = this.controller.orderId
    const scheduled = this.controller.scheduledFor
    const scheduledLabel = scheduled ? this.formatScheduledFor(scheduled) : null
    return html`
      <div class="panel thankyou" role="status" aria-live="polite">
        <div class="check">✓</div>
        <h2>Order placed!</h2>
        <p>
          ${scheduledLabel
            ? html`Scheduled for ${scheduledLabel}.`
            : html`We've started preparing your order.`}
        </p>
        ${orderId
          ? html`<div class="order-id" aria-label="Order reference">
              Order #${orderId}
            </div>`
          : nothing}
        <button @click=${() => void this.controller.start()}>
          Start a new order
        </button>
      </div>
    `
  }

  render() {
    const c = this.controller
    const complete = c.conversationState === 'complete'
    // In modal-thankyou mode the underlying panels stay visible
    // (frozen) and the thank-you screen pops as a modal on top, so
    // we don't hide menu/cart or swap out the chat panel on
    // `complete`. The default 'panel' mode swaps the chat slot.
    const thankyouModal = complete && this.thankyouRender === 'modal'
    const completeAsPanel = complete && this.thankyouRender !== 'modal'
    const showLog = this.panels.includes('log')
    const menuInPanels = this.panels.includes('menu')
    // chat in panels OR forced for the lifetime of a modifier-picker
    // hint that has no other surface to render in (Trello #111).
    const chatInPanels =
      this.panels.includes('chat') || this._chatForcedByModifierHint
    // Built-in mobile toggle fires only when both menu and chat are in
    // `panels` (single-panel mounts have nothing to toggle between),
    // the widget is at mobile width, and the host hasn't opted out.
    const mobileToggleOn =
      this.mobileToggle !== 'off' &&
      this._isMobile &&
      menuInPanels &&
      chatInPanels &&
      !completeAsPanel
    const showMenu =
      menuInPanels &&
      !completeAsPanel &&
      (!mobileToggleOn || this.mobileActivePanel === 'menu')
    const showChat =
      chatInPanels &&
      (!mobileToggleOn || this.mobileActivePanel === 'chat')
    // Deactivate the cart once the order is placed — including the floating
    // collapsible cart in thankyou-render="modal" mode, where the panels
    // otherwise stay mounted and the open drawer sits beside the modal.
    const showCart = this.panels.includes('cart') && !complete
    // Below MOBILE_MAX_WIDTH the cart renders as a sticky strip above the
    // panel row (full-width, qty badge always visible, regardless of which
    // Menu/Chat tab is active). On desktop, cart-collapsible (host opt-in)
    // keeps the legacy floating-circle behavior; otherwise inline panel.
    const cartCompactStrip = showCart && this._isMobile
    const cartFloating = showCart && this.cartCollapsible && !this._isMobile
    const cartInline = showCart && !cartCompactStrip && !cartFloating
    // Storefront info sits in the cart column (above the cart), so it
    // travels with the cart in every layout variant — inline panel,
    // floating drawer, mobile compact strip. Hidden entirely when the
    // host doesn't list `storefront-info` in `panels`.
    const storefrontInfo = this.panels.includes('storefront-info')
      ? html`<bb-storefront-info
          .storefrontName=${this.storefrontName}
          .operationalStatus=${this.operationalStatus}
          .openingHours=${this.openingHours}
          .timeZone=${this.storefrontTimeZone}
        ></bb-storefront-info>`
      : nothing
    return html`
      ${mobileToggleOn
        ? html`
            <div class="mobile-toggle" part="mobile-toggle" role="tablist" aria-label="Panel">
              <button
                type="button"
                role="tab"
                aria-selected=${this.mobileActivePanel === 'menu'}
                class=${this.mobileActivePanel === 'menu' ? 'active' : ''}
                @click=${() => { this.mobileActivePanel = 'menu' }}
              >Menu</button>
              <button
                type="button"
                role="tab"
                aria-selected=${this.mobileActivePanel === 'chat'}
                class=${this.mobileActivePanel === 'chat' ? 'active' : ''}
                @click=${() => { this.mobileActivePanel = 'chat' }}
              >Chat</button>
            </div>
          `
        : nothing}
      ${cartInline ? nothing : storefrontInfo}
      ${cartCompactStrip
        ? html`
            <bb-cart
              collapsible
              compact
              .conversationId=${c.conversationId}
              .cart=${c.cart}
              .fulfillmentType=${c.fulfillmentType}
              .hint=${c.hint}
              .busy=${c.busy}
              .complete=${c.conversationState === 'complete'}
              @cart-add=${this.onCartAdd}
              @cart-remove=${this.onCartRemove}
              @cart-clear=${this.onCartClear}
              @submit-order=${this.onSubmitOrder}
            ></bb-cart>
          `
        : nothing}
      <div class="layout">
        ${showLog
          ? html`
              <div class="panel log">
                <bb-log
                  .entries=${this.log}
                  @log-clear=${this.onLogClear}
                ></bb-log>
              </div>
            `
          : nothing}
        ${showMenu
          ? html`
              <div class="panel menu">
                <bb-menu
                  .api=${this.api}
                  .menuId=${this.menuId}
                  .storefrontId=${this.storefrontId}
                  @item-selected=${this.onMenuItemSelected}
                ></bb-menu>
              </div>
            `
          : nothing}
        ${completeAsPanel
          ? this.renderThankYou()
          : showChat
            ? html`<div class="panel chat">${this.renderChat()}</div>`
            : nothing}
        ${cartInline
          ? html`
              <div class="panel cart">
                ${storefrontInfo}
                <bb-cart
                  .conversationId=${c.conversationId}
                  .cart=${c.cart}
                  .fulfillmentType=${c.fulfillmentType}
                  .hint=${c.hint}
                  .busy=${c.busy}
                  .complete=${c.conversationState === 'complete'}
                  @cart-add=${this.onCartAdd}
                  @cart-remove=${this.onCartRemove}
                  @cart-clear=${this.onCartClear}
                  @submit-order=${this.onSubmitOrder}
                ></bb-cart>
              </div>
            `
          : nothing}
      </div>
      ${cartFloating
        ? html`
            <bb-cart
              collapsible
              .conversationId=${c.conversationId}
              .cart=${c.cart}
              .fulfillmentType=${c.fulfillmentType}
              .hint=${c.hint}
              .busy=${c.busy}
              .complete=${c.conversationState === 'complete'}
              @cart-add=${this.onCartAdd}
              @cart-remove=${this.onCartRemove}
              @cart-clear=${this.onCartClear}
              @submit-order=${this.onSubmitOrder}
            ></bb-cart>
          `
        : nothing}
      ${this.renderFulfillmentOverlay()}
      ${this.renderAddressOverlay()}
      ${this.renderContactOverlay()}
      ${this.renderSchedulingOverlay()}
      ${this.renderSubmitOverlay()}
      ${thankyouModal ? this.renderThankYouModal() : nothing}
      ${c.submitting
        ? html`
            <div
              class="submitting-overlay"
              part="submitting-overlay"
              role="status"
              aria-live="assertive"
            >
              <div class="spinner" part="submitting-spinner" aria-hidden="true"></div>
              <div class="label" part="submitting-label">Submitting your order…</div>
              <div class="sub" part="submitting-sub">Please don't close the tab.</div>
            </div>
          `
        : nothing}
    `
  }

  // Visually-modal popup for the fulfillment hint when fulfillmentRender
  // is 'overlay'. Not a real <dialog>: no focus trap. Dismissable via
  // Esc / backdrop click / Cancel button — dismissing hides the modal
  // locally for the current hint id; the next turn that emits a new
  // fulfillment hint reopens it (same pattern as the submit overlay).
  // Reuses <bb-chat-fulfillment> verbatim so inline and overlay produce
  // identical UI from the same source.
  // Tracks the id of a fulfillment / submit hint the user has dismissed
  // via Esc / backdrop / Cancel. The hint stays in controller state
  // (it's still true from the agent's POV); we just hide the modal
  // locally until a *new* turn produces a different hint id, at which
  // point the modal reappears.
  @state() private _submitOverlayDismissedFor: string | null = null
  @state() private _fulfillmentOverlayDismissedFor: string | null = null
  // Dismissal for the address dialog (delivery_address hint). Same
  // hide-locally-until-next-hint-id pattern as the fulfillment overlay.
  @state() private _addressOverlayDismissedFor: string | null = null
  // Dismissal for the contact-details overlay. Same hide-locally-until-
  // next-hint-id pattern. Trello #126: contact is a first-class
  // precondition with its own hint, no more slot-coupling to submit.
  @state() private _contactOverlayDismissedFor: string | null = null
  // Trello #192: when a fresh bot turn arrives carrying an
  // overlay-eligible hint AND substantive text, we hold the modal for
  // that turn so the user sees the bot reply first. The hold applies
  // only the first time we see a given (kind, hint-id) pair — so a hint
  // that persists across many turns isn't suppressed forever. The
  // controller's `botTurnSeq` is the per-turn key; `_heldForBotTurnSeq`
  // matches the current turn iff the held render path should bail.
  // `_heldOverlayHintIds` is the "we've held once for this hint" memo.
  @state() private _heldForBotTurnSeq: number | null = null
  private _lastSeenBotTurnSeq = 0
  private _heldOverlayHintIds = new Set<string>()
  // Dismissal for the scheduled-time overlay. Same pattern. Trello #131:
  // scheduling now has parity with the other blocking-precondition
  // overlays (fulfillment/contact/submit).
  @state() private _schedulingOverlayDismissedFor: string | null = null

  // True when the current overlay-rendered hint has been dismissed locally
  // (backdrop / Cancel / Esc). Used by the chat-panel suppress wiring so a
  // dismissed overlay falls back to the inline render — otherwise the user
  // is stranded with no way to fill the precondition (Trello #144).
  private isFulfillmentOverlayDismissed(): boolean {
    const ff = asFulfillment(this.controller.hint)
    return !!ff && this._fulfillmentOverlayDismissedFor === ff.id
  }
  private isContactOverlayDismissed(): boolean {
    const ch = asContactDetails(this.controller.hint)
    return !!ch && this._contactOverlayDismissedFor === ch.id
  }
  private isSchedulingOverlayDismissed(): boolean {
    const sc = asScheduledTime(this.controller.hint)
    return !!sc && this._schedulingOverlayDismissedFor === sc.id
  }
  private isSubmitOverlayDismissed(): boolean {
    const sb = asSubmit(this.controller.hint)
    return !!sb && this._submitOverlayDismissedFor === sb.id
  }

  // Trello #192: returns the (kind, hint-id) of the current hint if it
  // would render as an overlay under the host's current render-mode
  // attributes; otherwise null. Used by the new-bot-turn hold logic.
  private currentOverlayEligibleHint(): { kind: string; id: string } | null {
    const h = this.controller.hint
    if (!h) return null
    if (this.contactRender === 'overlay') {
      const ch = asContactDetails(h)
      if (ch?.id) return { kind: 'contact', id: ch.id }
    }
    if (this.submitRender === 'overlay') {
      const sb = asSubmit(h)
      if (sb?.id) return { kind: 'submit', id: sb.id }
    }
    if (this.fulfillmentRender === 'overlay') {
      const ff = asFulfillment(h)
      if (ff?.id) return { kind: 'fulfillment', id: ff.id }
    }
    if (this.schedulingRender === 'overlay') {
      const sc = asScheduledTime(h)
      if (sc?.id) return { kind: 'scheduling', id: sc.id }
    }
    return null
  }

  // True iff an overlay-eligible hint just arrived on the current bot
  // turn and the user hasn't seen it before. Each render*Overlay() bails
  // when this is true; the chat-panel suppress wires also bail so the
  // inline form renders inside the transcript as a fallback — the user
  // still has a way to act, just not via a focus-stealing modal.
  private isOverlayHeldForCurrentTurn(): boolean {
    return (
      this._heldForBotTurnSeq !== null &&
      this._heldForBotTurnSeq === this.controller.botTurnSeq
    )
  }

  // Watches `controller.botTurnSeq`. On every transition (a new bot
  // utterance applied to state), if the latest bot message has
  // substantive text AND the current hint would render as an overlay,
  // hold the overlay for this turn — but only the first time we see
  // this exact (kind, id) pair, so a persistent hint isn't suppressed
  // turn after turn. Emits `modal.held` so the log explains the gap.
  private _maybeHoldOverlayForNewBotTurn(): void {
    const seq = this.controller.botTurnSeq
    if (seq === this._lastSeenBotTurnSeq) return
    this._lastSeenBotTurnSeq = seq
    const last = this.controller.messages[this.controller.messages.length - 1]
    const hasBotText =
      last?.who === 'bot' && !!last.text && last.text.trim().length > 0
    if (!hasBotText) return
    const eligible = this.currentOverlayEligibleHint()
    if (!eligible) return
    // Blocking gates whose modal is the direct subject of the bot's text
    // are exempt from the new-bot-turn hold (Trello #203). For contact
    // details and the submit-confirm review, the bot's prose introduces
    // the modal by name ("I need your phone number" → contact modal;
    // "to review your order" → submit modal) — these are not interrupting
    // an unrelated reply, they ARE the reply. Holding them leaves the
    // customer staring at a chat message that references a surface they
    // can't see, with no clear way to act. The hold's original target
    // (Trello #192) was the fulfillment overlay flashing over an
    // unrelated greeting; that case still applies and still holds.
    if (eligible.kind === 'contact' || eligible.kind === 'submit') return
    const key = `${eligible.kind}:${eligible.id}`
    if (this._heldOverlayHintIds.has(key)) return
    this._heldOverlayHintIds.add(key)
    this._heldForBotTurnSeq = seq
    this.postEvent('modal.held', {
      kind: eligible.kind,
      hintId: eligible.id,
      reason: 'new-bot-turn',
    })
  }

  // ─ Client-event telemetry ──────────────────────────────────────────
  // Dedup key set for one-shot events (modal.shown / modal.suppressed /
  // cta.disabled). Lit re-renders many times per turn — without this we'd
  // log the same modal-shown row on every property tick.
  private _firedEvents = new Set<string>()

  private postEvent(
    event: string,
    data?: Record<string, unknown> | null,
  ): void {
    const cid = this.controller.conversationId
    if (!cid) return
    void this.api?.postClientEvent(cid, event, data ?? null)
  }

  private postEventOnce(
    key: string,
    event: string,
    data?: Record<string, unknown> | null,
  ): void {
    if (this._firedEvents.has(key)) return
    this._firedEvents.add(key)
    this.postEvent(event, data)
  }

  // Mirror of the actual render decision in renderXxxOverlay, used by
  // hint.classified so the log records the client's intent even when the
  // overlay never reaches the DOM.
  private predictRenderAs(
    kind: HintKind,
    hintId: string | null,
  ): string {
    if (!kind) return 'no-hint'
    if (kind === 'fulfillment') {
      if (this.fulfillmentRender !== 'overlay') return 'inline'
      if (this._fulfillmentOverlayDismissedFor === hintId)
        return 'suppressed-already-dismissed'
      return 'overlay'
    }
    if (kind === 'delivery_address') {
      if (!this.renderHints) return 'suppressed-render-hints-false'
      if (this._addressOverlayDismissedFor === hintId)
        return 'suppressed-already-dismissed'
      return 'overlay'
    }
    if (kind === 'submit') {
      if (this.submitRender !== 'overlay') return 'inline'
      if (this._submitOverlayDismissedFor === hintId)
        return 'suppressed-already-dismissed'
      return 'overlay'
    }
    return 'inline'
  }

  private onVisibilityChange = () => {
    if (typeof document !== 'undefined' && document.hidden) {
      this.postEvent('page.hidden')
    }
  }

  // Single sink for every clickable affordance across child components.
  // Children bubble `widget-affordance {kind, context?}` and the widget
  // posts a single client_event with the same payload — no per-kind
  // wiring in this file. When pii-logging=false, address strings inside
  // the context are downgraded to {length} so we still see "user typed
  // something" without persisting the address.
  //
  // It also feeds the controller's ui-event buffer (Trello #166) when
  // the affordance produces no visible bot response of its own — silent
  // taps the LLM is otherwise blind to. The buffer flushes on the next
  // typed utterance, so anaphora like "never mind" / "cancel that"
  // resolves to the right thing. `uiEventStringFor` returns null for
  // affordances the LLM doesn't need (already-narrated cart mutations,
  // pure UI plumbing like cart-expand) — those still go to /client_events
  // for the log timeline.
  private onAffordance = (e: Event) => {
    const detail = (e as CustomEvent).detail as
      | {
          kind?: string
          context?: string | Record<string, unknown>
        }
      | undefined
    if (!detail?.kind) return
    this.postEvent('affordance.clicked', {
      kind: detail.kind,
      ...(detail.context !== undefined
        ? { context: this.redactContext(detail.context) }
        : {}),
    })
    const narration = this.uiEventStringFor(detail.kind, detail.context)
    if (narration) this.controller.pushUiEvent(narration)
  }

  // Map a widget-affordance kind+context to the one-line English string
  // the LLM will see, prefixed in brackets above the next typed
  // utterance. Customer as subject, active voice, past tense, item
  // names verbatim. Returning null skips the buffer for this affordance
  // (used for kinds whose effect is already visible in cart / fulfillment
  // state, or pure UI plumbing the LLM has no use for).
  private uiEventStringFor(
    kind: string,
    context: string | Record<string, unknown> | undefined,
  ): string | null {
    const item =
      context && typeof context === 'object' && typeof context.item === 'string'
        ? context.item
        : null
    switch (kind) {
      case 'modifier-cancel':
        return item
          ? `customer closed the modifier picker for "${item}" without adding it to the cart`
          : 'customer closed a modifier picker without adding the item to the cart'
      case 'menu-tile':
        // Only narrate when the tap opens a picker (no immediate cart
        // add). Without a way to tell at this point, narrate every
        // tile tap; if the tap leads to an add_to_cart the LLM sees
        // the cart update too, so the extra context is at worst
        // redundant. Cheap.
        return item ? `customer tapped on "${item}" on the menu` : null
      default:
        return null
    }
  }

  private redactContext(
    context: string | Record<string, unknown>,
  ): string | Record<string, unknown> {
    if (this.piiLogging) return context
    if (typeof context !== 'object' || context === null) return context
    const addr = context.address
    if (typeof addr === 'string') {
      return { ...context, address: undefined, addressLength: addr.length }
    }
    return context
  }

  // <bb-chat-contact> bubbles this on Continue with the validated phone
  // (and optional email). We POST /contact via the controller (the
  // existing `setContact` carries a race-guard so a subsequent
  // submitOrder awaits this), then send the same chained utterance the
  // fulfillment path uses so berrybot re-evaluates the precondition
  // chain and emits the next hint (submit_order). Mirrors the
  // controller's set-fulfillment then "Okay, I'm ready to check out."
  // pattern — same shape, different precondition.
  private onSetContact = (e: Event) => {
    const detail = (e as CustomEvent).detail as
      | { phone?: string; email?: string }
      | undefined
    if (!detail?.phone) return
    this.postEvent('contact.submitted', {
      hasPhone: true,
      hasEmail: !!detail.email,
      ...(this.piiLogging
        ? { phone: detail.phone, email: detail.email ?? '' }
        : {
            phoneLength: detail.phone.length,
            emailLength: detail.email?.length ?? 0,
          }),
    })
    void (async () => {
      await this.controller.setContact({
        phone: detail.phone ?? null,
        email: detail.email ?? null,
      })
      // Chained checkout utterance: the agent re-evaluates state and
      // build_hint surfaces submit_order on the same turn.
      await this.controller.send("Okay, I'm ready to check out.")
    })()
  }

  private renderFulfillmentOverlay() {
    if (this.fulfillmentRender !== 'overlay') return nothing
    const ff = asFulfillment(this.controller.hint)
    if (!ff) return nothing
    if (this.isOverlayHeldForCurrentTurn()) return nothing
    if (this._fulfillmentOverlayDismissedFor === ff.id) {
      this.postEventOnce(
        `modal.suppressed:fulfillment:${ff.id}`,
        'modal.suppressed',
        { kind: 'fulfillment', hintId: ff.id, reason: 'already-dismissed' },
      )
      return nothing
    }
    this.postEventOnce(
      `modal.shown:fulfillment:${ff.id}`,
      'modal.shown',
      { kind: 'fulfillment', hintId: ff.id },
    )
    return html`
      <div class="bb-modal" part="modal modal-fulfillment" role="region" aria-label="Fulfillment">
        <div
          class="bb-modal-backdrop"
          part="modal-backdrop"
          @click=${() => this.onFulfillmentOverlayDismiss('backdrop')}
        ></div>
        <div class="bb-modal-card" part="modal-card">
          <div class="bb-modal-title" part="modal-title">
            How would you like your order?
          </div>
          <bb-chat-fulfillment
            .hint=${ff}
            .active=${true}
            ?disabled=${this.controller.busy}
            layout=${this.fulfillmentOverlayLayout}
            exportparts="affordance,opt,opt-pickup,opt-delivery,opt-icon,opt-body,opt-label,opt-sub,delivery-form,delivery-input,suggestions,suggestion,suggestion-button,sample-link,deliver-button"
            @set-fulfillment=${this.onSetFulfillment}
            @fetch-sample-address=${this.onFetchSampleAddress}
            @fetch-address-suggestions=${this.onFetchAddressSuggestions}
        @fetch-place-details=${this.onFetchPlaceDetails}
          ></bb-chat-fulfillment>
          <div class="modal-cancel-row" part="modal-cancel-row">
            <button
              class="modal-cancel"
              part="modal-cancel"
              @click=${() => this.onFulfillmentOverlayDismiss('cancel')}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    `
  }

  // Visually-modal popup for the contact-details hint when
  // contact-render="overlay". Mirrors renderFulfillmentOverlay exactly
  // (Trello #126): the chat panel suppresses its inline render in this
  // mode, the overlay shows the same <bb-chat-contact> form, the
  // host page can re-skin via the exported shadow parts. Dismiss hides
  // the modal locally for this hint id — the conversation state still
  // has it set; a new turn producing a different id reopens it.
  private renderContactOverlay() {
    if (this.contactRender !== 'overlay') return nothing
    const ch = asContactDetails(this.controller.hint)
    if (!ch) return nothing
    if (this.isOverlayHeldForCurrentTurn()) return nothing
    if (this._contactOverlayDismissedFor === ch.id) {
      this.postEventOnce(
        `modal.suppressed:contact:${ch.id}`,
        'modal.suppressed',
        { kind: 'contact', hintId: ch.id, reason: 'already-dismissed' },
      )
      return nothing
    }
    this.postEventOnce(
      `modal.shown:contact:${ch.id}`,
      'modal.shown',
      { kind: 'contact', hintId: ch.id },
    )
    return html`
      <div
        class="bb-modal"
        part="modal modal-contact"
        role="region"
        aria-label="Contact details"
      >
        <div
          class="bb-modal-backdrop"
          part="modal-backdrop"
          @click=${() => this.onContactOverlayDismiss('backdrop')}
        ></div>
        <div class="bb-modal-card" part="modal-card">
          <div class="bb-modal-title" part="modal-title">
            How can we reach you?
          </div>
          <bb-chat-contact
            .hint=${ch}
            .active=${true}
            ?disabled=${this.controller.busy}
            layout=${this.contactOverlayLayout}
            exportparts="form,intro,field,field-phone,field-email,label,label-phone,label-email,input,input-phone,input-email,hint,error,error-phone,cta,btn-spinner"
          ></bb-chat-contact>
          <div class="modal-cancel-row" part="modal-cancel-row">
            <button
              class="modal-cancel"
              part="modal-cancel"
              @click=${() => this.onContactOverlayDismiss('cancel')}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    `
  }

  private onContactOverlayDismiss = (
    via: 'backdrop' | 'cancel' | 'esc' = 'backdrop',
  ) => {
    const ch = asContactDetails(this.controller.hint)
    this._contactOverlayDismissedFor = ch?.id ?? null
    this.postEvent('modal.dismissed', {
      kind: 'contact',
      hintId: ch?.id ?? null,
      via,
    })
  }

  // Visually-modal popup for the scheduled-time hint when
  // scheduling-render="overlay". Mirrors renderContactOverlay (Trello
  // #131) — same shape, parts, dismiss model. Inside, the same
  // <bb-chat-scheduled-time> the inline path uses; emits
  // `schedule-order` / `schedule-decline` which the widget catches via
  // its global listeners.
  private renderSchedulingOverlay() {
    if (this.schedulingRender !== 'overlay') return nothing
    const sc = asScheduledTime(this.controller.hint)
    if (!sc) return nothing
    if (this.isOverlayHeldForCurrentTurn()) return nothing
    if (this._schedulingOverlayDismissedFor === sc.id) {
      this.postEventOnce(
        `modal.suppressed:scheduling:${sc.id}`,
        'modal.suppressed',
        { kind: 'scheduling', hintId: sc.id, reason: 'already-dismissed' },
      )
      return nothing
    }
    this.postEventOnce(
      `modal.shown:scheduling:${sc.id}`,
      'modal.shown',
      { kind: 'scheduling', hintId: sc.id },
    )
    return html`
      <div
        class="bb-modal"
        part="modal modal-scheduling"
        role="region"
        aria-label="Schedule order"
      >
        <div
          class="bb-modal-backdrop"
          part="modal-backdrop"
          @click=${() => this.onSchedulingOverlayDismiss('backdrop')}
        ></div>
        <div class="bb-modal-card" part="modal-card">
          <bb-chat-scheduled-time
            .hint=${sc}
            .active=${true}
            ?disabled=${this.controller.busy}
            layout=${this.schedulingOverlayLayout}
            exportparts="sched,title,suggestion,field,field-day,field-time,label,label-day,label-time,select,select-day,select-time,cta,cta-schedule,cta-decline"
          ></bb-chat-scheduled-time>
          <div class="modal-cancel-row" part="modal-cancel-row">
            <button
              class="modal-cancel"
              part="modal-cancel"
              @click=${() => this.onSchedulingOverlayDismiss('cancel')}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    `
  }

  private onSchedulingOverlayDismiss = (
    via: 'backdrop' | 'cancel' | 'esc' = 'backdrop',
  ) => {
    const sc = asScheduledTime(this.controller.hint)
    this._schedulingOverlayDismissedFor = sc?.id ?? null
    this.postEvent('modal.dismissed', {
      kind: 'scheduling',
      hintId: sc?.id ?? null,
      via,
    })
  }

  private onScheduleOrder = (e: Event) => {
    const detail = (e as CustomEvent).detail as { iso?: string } | undefined
    if (!detail?.iso) return
    void this.controller.send(`Schedule it for ${detail.iso}`)
  }

  private onScheduleDecline = () => {
    void this.controller.send('No thanks, not this time.')
  }

  private onFulfillmentOverlayDismiss = (
    via: 'backdrop' | 'cancel' | 'esc' = 'backdrop',
  ) => {
    const ff = asFulfillment(this.controller.hint)
    this._fulfillmentOverlayDismissedFor = ff?.id ?? null
    this.postEvent('modal.dismissed', {
      kind: 'fulfillment',
      hintId: ff?.id ?? null,
      via,
    })
  }

  // Address dialog — renders when the server emits
  // input_needed:delivery_address (FT=delivery + no valid address yet).
  // The dialog has no prefill. It restates the assistant's last message
  // as a helper line: the backend already explained what went wrong
  // (e.g. "I just need the street name and house number"), but the
  // overlay backdrop dims the chat transcript, so we surface it here
  // where the user is actually looking. See req_fulfillment_removal.md.
  //
  // Suppressed when render-hints is false: the host has explicitly opted into
  // typed-only UX (voice-equivalent), so an overlay would block the
  // composer. The customer recovers by typing the new address; the
  // LLM picks it up from fulfillment_context state.
  private renderAddressOverlay() {
    const addr = asDeliveryAddress(this.controller.hint)
    if (!this.renderHints) {
      if (addr) {
        this.postEventOnce(
          `modal.suppressed:address:${addr.id}`,
          'modal.suppressed',
          {
            kind: 'address',
            hintId: addr.id,
            reason: 'render-hints-false',
          },
        )
      }
      return nothing
    }
    if (!addr) return nothing
    if (this._addressOverlayDismissedFor === addr.id) {
      this.postEventOnce(
        `modal.suppressed:address:${addr.id}`,
        'modal.suppressed',
        { kind: 'address', hintId: addr.id, reason: 'already-dismissed' },
      )
      return nothing
    }
    this.postEventOnce(
      `modal.shown:address:${addr.id}`,
      'modal.shown',
      { kind: 'address', hintId: addr.id },
    )
    // The bot's last message restated as a helper line — the backdrop
    // dims the chat, so the assistant's reason for the re-prompt has to
    // be surfaced here where the user is looking.
    const explanation = [...(this.controller.messages ?? [])]
      .reverse()
      .find((m) => m.who === 'bot')?.text
    // The form INSIDE the modal is the very same <bb-address-form> used
    // inline by the fulfillment affordance — only the envelope differs.
    // Its Confirm emits `set-fulfillment` (with parsed_address), caught
    // by the same onSetFulfillment handler, so the recovery path now
    // matches the primary path. The fetch-* autocomplete events are
    // wired here too (the element isn't a <bb-chat-fulfillment>, so it
    // doesn't inherit those listeners).
    return html`
      <div class="bb-modal" part="modal modal-address" role="region" aria-label="Delivery address">
        <div
          class="bb-modal-backdrop"
          part="modal-backdrop"
          @click=${() => this.onAddressOverlayDismiss('backdrop')}
        ></div>
        <div class="bb-modal-card" part="modal-card">
          <div class="bb-modal-title" part="modal-title">
            Delivery address
          </div>
          ${explanation
            ? html`<div class="bb-modal-hint" part="modal-hint">
                ${explanation}
              </div>`
            : nothing}
          <bb-address-form
            part="address-form"
            .disabled=${this.controller.busy}
            .resetKey=${addr.id}
            @set-fulfillment=${this.onSetFulfillment}
            @fetch-address-suggestions=${this.onFetchAddressSuggestions}
            @fetch-place-details=${this.onFetchPlaceDetails}
            @fetch-sample-address=${this.onFetchSampleAddress}
          ></bb-address-form>
          <div class="modal-cancel-row" part="modal-cancel-row">
            <button
              class="modal-cancel"
              part="modal-cancel"
              @click=${() => this.onAddressOverlayDismiss('cancel')}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    `
  }

  private onAddressOverlayDismiss = (
    via: 'backdrop' | 'cancel' | 'esc' = 'backdrop',
  ) => {
    const addr = asDeliveryAddress(this.controller.hint)
    this._addressOverlayDismissedFor = addr?.id ?? null
    this.postEvent('modal.dismissed', {
      kind: 'address',
      hintId: addr?.id ?? null,
      via,
    })
  }

  // Window-level Esc dismiss for the fulfillment + submit overlays.
  // Picks the topmost visible overlay (submit > fulfillment if both
  // were somehow open). Thank-you modal is intentionally excluded:
  // the order is placed; the only sane exit is "Start a new order".
  private onWindowKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return
    if (
      this.submitRender === 'overlay'
      && asSubmit(this.controller.hint)
      && this._submitOverlayDismissedFor !== asSubmit(this.controller.hint)?.id
    ) {
      e.preventDefault()
      this.onSubmitOverlayDismiss('esc')
      return
    }
    const addr = asDeliveryAddress(this.controller.hint)
    if (addr && this._addressOverlayDismissedFor !== addr.id) {
      e.preventDefault()
      this.onAddressOverlayDismiss('esc')
      return
    }
    if (
      this.contactRender === 'overlay'
      && asContactDetails(this.controller.hint)
      && this._contactOverlayDismissedFor
        !== asContactDetails(this.controller.hint)?.id
    ) {
      e.preventDefault()
      this.onContactOverlayDismiss('esc')
      return
    }
    if (
      this.schedulingRender === 'overlay'
      && asScheduledTime(this.controller.hint)
      && this._schedulingOverlayDismissedFor
        !== asScheduledTime(this.controller.hint)?.id
    ) {
      e.preventDefault()
      this.onSchedulingOverlayDismiss('esc')
      return
    }
    if (
      this.fulfillmentRender === 'overlay'
      && asFulfillment(this.controller.hint)
      && this._fulfillmentOverlayDismissedFor
        !== asFulfillment(this.controller.hint)?.id
    ) {
      e.preventDefault()
      this.onFulfillmentOverlayDismiss('esc')
    }
  }

  // Visually-modal popup for the submit-order hint when submitRender is
  // 'overlay'. The chat panel suppresses its inline "Place my order"
  // affordance so only one CTA is live. Body shows a line-item recap
  // (name × qty, with price when present), the fulfillment summary
  // (Pickup / Delivery to <address>), the confirm CTA, and a
  // "Keep shopping" dismiss. Confirm dispatches the same utterance
  // the inline button does — same backend path, just different
  // presentation. Dismiss hides the modal locally for this hint id
  // (the conversation state still has the hint set; a new turn
  // producing a different hint id reopens the modal).
  private renderSubmitOverlay() {
    const submit = asSubmit(this.controller.hint)
    if (this.submitRender !== 'overlay') {
      if (submit) {
        this.postEventOnce(
          `modal.suppressed:submit:${submit.id}`,
          'modal.suppressed',
          { kind: 'submit', hintId: submit.id, reason: 'render-inline' },
        )
      }
      return nothing
    }
    if (!submit) return nothing
    if (this.isOverlayHeldForCurrentTurn()) return nothing
    if (this._submitOverlayDismissedFor === submit.id) {
      this.postEventOnce(
        `modal.suppressed:submit:${submit.id}`,
        'modal.suppressed',
        { kind: 'submit', hintId: submit.id, reason: 'already-dismissed' },
      )
      return nothing
    }
    this.postEventOnce(
      `modal.shown:submit:${submit.id}`,
      'modal.shown',
      { kind: 'submit', hintId: submit.id },
    )
    const ctaDisabledReason = this.controller.busy ? 'busy' : null
    if (ctaDisabledReason) {
      this.postEventOnce(
        `cta.disabled:submit:${submit.id}:${ctaDisabledReason}`,
        'cta.disabled',
        {
          cta: 'submit-overlay',
          hintId: submit.id,
          reason: ctaDisabledReason,
        },
      )
    }
    const cart = this.controller.cart
    const items = (cart?.items ?? []) as unknown as Array<Record<string, unknown>>
    const ft = String(this.controller.fulfillmentType ?? '')
    const addr = this.controller.cart
      ? String(
          (this.controller as unknown as Record<string, unknown>).address ?? '',
        )
      : ''
    const fulfillmentLabel =
      ft === 'pick_up'
        ? 'Pickup'
        : ft === 'delivery'
        ? addr
          ? `Delivery to ${addr}`
          : 'Delivery'
        : ''
    return html`
      <div
        class="bb-modal"
        part="modal modal-submit"
        role="region"
        aria-label="Confirm order"
      >
        <div
          class="bb-modal-backdrop"
          part="modal-backdrop"
          @click=${() => this.onSubmitOverlayDismiss('backdrop')}
        ></div>
        <div class="bb-modal-card" part="modal-card submit-card">
          <div class="bb-modal-title" part="modal-title">
            Confirm your order
          </div>
          ${items.length
            ? html`
                <ul class="submit-items" part="submit-items">
                  ${items.map(
                    (line) => html`
                      <li class="submit-item" part="submit-item">
                        <span class="submit-item-qty" part="submit-item-qty"
                          >${Number(line.quantity) || 1}×</span
                        >
                        <span class="submit-item-name" part="submit-item-name"
                          >${String(line.name ?? line.item ?? '')}</span
                        >
                        ${line.price
                          ? html`<span
                              class="submit-item-price"
                              part="submit-item-price"
                              >${String(line.price)}</span
                            >`
                          : nothing}
                      </li>
                    `,
                  )}
                </ul>
              `
            : nothing}
          ${fulfillmentLabel
            ? html`<div
                class="submit-fulfillment"
                part="submit-fulfillment"
              >
                ${fulfillmentLabel}
              </div>`
            : nothing}
          <div class="submit-actions" part="submit-actions">
            <button
              class="submit-cta"
              part="submit-cta"
              ?disabled=${this.controller.busy}
              @click=${this.onSubmitOverlayConfirm}
            >
              ${this.controller.submitting
                ? html`<span
                      class="bb-btn-spinner"
                      part="btn-spinner"
                      aria-hidden="true"
                    ></span
                    >Placing order…`
                : 'Place my order'}
            </button>
            <button
              class="submit-dismiss"
              part="submit-dismiss"
              @click=${() => this.onSubmitOverlayDismiss('keep-shopping')}
            >
              Keep shopping
            </button>
          </div>
        </div>
      </div>
    `
  }

  private onSubmitOverlayConfirm = () => {
    // Direct /orders/submit (same path as the cart-side button) —
    // NOT a chat utterance. The overlay is itself the review: the
    // customer has just seen the cart recap + fulfillment summary.
    // Going through `send("Yes, place my order")` would trip the
    // agent's "I can't submit without reviewing first" gate.
    this.appendLog({
      kind: 'note',
      subject: 'order.submit-clicked',
      text: this.controller.conversationId ?? '',
    })
    this.postEvent('affordance.clicked', { kind: 'submit-overlay' })
    void this.controller.submitOrder()
  }

  private onSubmitOverlayDismiss = (
    via: 'backdrop' | 'keep-shopping' | 'esc' = 'backdrop',
  ) => {
    const submit = asSubmit(this.controller.hint)
    this._submitOverlayDismissedFor = submit?.id ?? null
    this.postEvent('modal.dismissed', {
      kind: 'submit',
      hintId: submit?.id ?? null,
      via,
    })
  }

  // Order-complete confirmation as a modal (thankyou-render="modal").
  // The underlying panels stay visible behind the scrim — same chrome
  // contract as ai-ordering-fe's Thank You modal. Backdrop is
  // non-dismissible; the only exit is "Start a new order", which
  // calls controller.start() to reset.
  private renderThankYouModal() {
    const orderId = this.controller.orderId
    this.postEventOnce(
      `modal.shown:thankyou:${orderId ?? '_'}`,
      'modal.shown',
      { kind: 'thankyou', orderId: orderId ?? null },
    )
    return html`
      <div
        class="bb-modal"
        part="modal modal-thankyou"
        role="status"
        aria-live="polite"
        aria-label="Order placed"
      >
        <div class="bb-modal-backdrop" part="modal-backdrop"></div>
        <div class="bb-modal-card bb-modal-thankyou" part="modal-card thankyou-card">
          <div class="check" part="thankyou-check">✓</div>
          <h2 part="thankyou-headline">Order placed!</h2>
          <p part="thankyou-body">
            ${(() => {
              const s = this.controller.scheduledFor
              const label = s ? this.formatScheduledFor(s) : null
              return label
                ? html`Scheduled for ${label}.`
                : html`We've started preparing your order.`
            })()}
          </p>
          ${orderId
            ? html`<div
                class="order-id"
                part="thankyou-order-id"
                aria-label="Order reference"
              >
                Order #${orderId}
              </div>`
            : nothing}
          <button
            part="thankyou-cta"
            @click=${() => {
              this.postEvent('affordance.clicked', { kind: 'thankyou-restart' })
              this._firedEvents.clear()
              void this.controller.start()
            }}
          >
            Start a new order
          </button>
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bb-widget': BbWidget
  }
}
